import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TrendingUp, Sprout, Brain, Calendar, Activity } from 'lucide-react';
import { format, addDays, startOfMonth, endOfMonth, eachMonthOfInterval, addMonths } from 'date-fns';
import AIProductionForecast from './AIProductionForecast';
import StandardYieldForecast from './StandardYieldForecast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Production yield per room per flush
const FLUSH_1_TOTAL = 5500; // 5.5 tonnes
const FLUSH_2_TOTAL = 3500; // 3.5 tonnes

const ForecastDashboard = ({ userRole = 'owner' }) => {
  const [loading, setLoading] = useState(true);
  const [compostBatches, setCompostBatches] = useState([]);
  const [roomCycles, setRoomCycles] = useState([]);
  const [production, setProduction] = useState([]);
  const [spawningForecast, setSpawningForecast] = useState([]);
  const [monthWiseSpawnings, setMonthWiseSpawnings] = useState([]);
  const [productionForecast, setProductionForecast] = useState({
    next15Days: { predicted: 0, actual: 0, flush1: 0, flush2: 0, rooms: 0 },
    nextMonth: { predicted: 0, actual: 0, flush1: 0, flush2: 0, rooms: 0 },
    next60Days: { predicted: 0, actual: 0, flush1: 0, flush2: 0, rooms: 0 },
    monthWise: []
  });

  // Role-based visibility:
  // - Owner: ALL forecasts with TABS (Spawning, Production, AI)
  // - Grower: Both forecasts (Spawning + Production) on same page
  // - Composting Supervisor: Only Spawning/Composting forecast
  // - Growing Supervisor: Only Production forecast (NOT spawning)
  const showSpawningForecast = userRole === 'composting_supervisor' || userRole === 'owner' || userRole === 'grower';
  const showProductionForecast = userRole === 'growing_supervisor' || userRole === 'owner' || userRole === 'grower';
  const showAIForecast = userRole === 'owner';
  const useTabbedLayout = userRole === 'owner';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [batchesRes, cyclesRes, productionRes] = await Promise.all([
        axios.get(`${API}/compost/batches`, { headers }),
        axios.get(`${API}/room-cycles?status=active`, { headers }),
        axios.get(`${API}/production`, { headers })
      ]);

      setCompostBatches(batchesRes.data);
      // Handle both array and object with 'cycles' key response formats
      const cyclesData = Array.isArray(cyclesRes.data) ? cyclesRes.data : (cyclesRes.data.cycles || []);
      setRoomCycles(cyclesData);
      // Production endpoint returns { records, total, total_weight_kg } — extract array
      const productionData = Array.isArray(productionRes.data)
        ? productionRes.data
        : (productionRes.data.records || []);
      setProduction(productionData);

      calculateForecasts(batchesRes.data, cyclesData, productionData);
    } catch (error) {
      console.error('Failed to fetch forecast data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateForecasts = (batches, cycles, productionData) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // ===== CALCULATE SPAWNING FORECAST FROM COMPOSTING =====
    // Active compost batches + future prewetting dates
    const spawningList = [];
    const activeBatches = batches.filter(b => b.status === 'active');
    
    // Add spawning dates from active batches
    activeBatches.forEach(batch => {
      const startDate = new Date(batch.start_date);
      const spawningDate = addDays(startDate, 19); // Day 20 is spawning ready
      const daysUntil = Math.ceil((spawningDate - today) / (1000 * 60 * 60 * 24));
      
      if (daysUntil >= 0 && daysUntil <= 90) {
        spawningList.push({
          batchName: batch.batch_name,
          date: spawningDate,
          prewettingDate: startDate,
          daysUntil: daysUntil,
          source: 'active_batch'
        });
      }
    });
    
    // Calculate future spawnings from planned prewetting dates (every 3 days)
    // Find the latest prewetting date to project from
    let latestPrewettingDate = null;
    if (activeBatches.length > 0) {
      const sortedBatches = [...activeBatches].sort((a, b) => 
        new Date(b.start_date) - new Date(a.start_date)
      );
      latestPrewettingDate = new Date(sortedBatches[0].start_date);
    }
    
    // Generate next 50 prewetting dates (every 3 days) to show at least 50 spawnings
    if (latestPrewettingDate) {
      for (let i = 1; i <= 50; i++) {
        const prewettingDate = addDays(latestPrewettingDate, i * 3);
        const spawningDate = addDays(prewettingDate, 19); // 20 days for composting cycle
        const daysUntil = Math.ceil((spawningDate - today) / (1000 * 60 * 60 * 24));
        
        if (spawningDate > today && daysUntil <= 180) { // Extended to 180 days (6 months)
          spawningList.push({
            batchName: `Projected Batch #${batches.length + i}`,
            prewettingDate: prewettingDate,
            date: spawningDate,
            daysUntil: daysUntil,
            source: 'planned'
          });
        }
      }
    }
    
    // Sort by date
    spawningList.sort((a, b) => a.date - b.date);
    setSpawningForecast(spawningList);
    
    // ===== CALCULATE MONTH-WISE SPAWNING COUNT =====
    const monthSpawnings = [];
    const months = eachMonthOfInterval({
      start: startOfMonth(today),
      end: addMonths(today, 6) // Extended to 6 months
    });
    
    months.forEach(monthStart => {
      const monthEnd = endOfMonth(monthStart);
      const spawningsInMonth = spawningList.filter(s => 
        s.date >= monthStart && s.date <= monthEnd
      );
      monthSpawnings.push({
        month: format(monthStart, 'MMMM yyyy'),
        monthShort: format(monthStart, 'MMM'),
        count: spawningsInMonth.length,
        spawnings: spawningsInMonth
      });
    });
    setMonthWiseSpawnings(monthSpawnings);

    // ===== CALCULATE PRODUCTION FORECAST =====
    // Production comes from TWO sources:
    // 1. CURRENT ROOM CYCLES (rooms already spawned and growing)
    // 2. FUTURE SPAWNINGS (from composting schedule)
    
    const in15Days = addDays(today, 15);
    const in30Days = addDays(today, 30);
    const in60Days = addDays(today, 60);
    
    let forecast15 = { predicted: 0, flush1: 0, flush2: 0, rooms: 0 };
    let forecast30 = { predicted: 0, flush1: 0, flush2: 0, rooms: 0 };
    let forecast60 = { predicted: 0, flush1: 0, flush2: 0, rooms: 0 };
    
    // Track all production items for month-wise breakdown
    const allProductionItems = [];
    
    // ===== SOURCE 1: CURRENT ROOM CYCLES =====
    // These are rooms that have already been spawned and are in various phases
    cycles.forEach(cycle => {
      const cycleStart = new Date(cycle.start_date);
      const currentDay = cycle.current_day || Math.floor((today - cycleStart) / (1000 * 60 * 60 * 24)) + 1;
      
      // Calculate flush dates from cycle start
      const flush1StartDate = addDays(cycleStart, 32); // Day 33
      const flush2StartDate = addDays(cycleStart, 40); // Day 41
      
      const roomName = cycle.room_name || `Room ${cycle.room_id}`;
      const batchName = cycle.batch_name || 'Current Batch';
      
      // Add Flush 1 if not yet completed (before Day 37)
      if (currentDay < 37 && flush1StartDate >= today) {
        allProductionItems.push({
          batchName: `${roomName} - ${batchName}`,
          spawningDate: cycleStart,
          flushDate: flush1StartDate,
          flush: 'Flush 1',
          production: FLUSH_1_TOTAL,
          source: 'room_cycle',
          roomId: cycle.room_id,
          currentDay: currentDay
        });
        
        if (flush1StartDate <= in15Days) {
          forecast15.predicted += FLUSH_1_TOTAL;
          forecast15.flush1 += FLUSH_1_TOTAL;
          forecast15.rooms++;
        }
        if (flush1StartDate <= in30Days) {
          forecast30.predicted += FLUSH_1_TOTAL;
          forecast30.flush1 += FLUSH_1_TOTAL;
          forecast30.rooms++;
        }
        if (flush1StartDate <= in60Days) {
          forecast60.predicted += FLUSH_1_TOTAL;
          forecast60.flush1 += FLUSH_1_TOTAL;
          forecast60.rooms++;
        }
      }
      
      // Add Flush 2 if not yet completed (before Day 45)
      if (currentDay < 45 && flush2StartDate >= today) {
        allProductionItems.push({
          batchName: `${roomName} - ${batchName}`,
          spawningDate: cycleStart,
          flushDate: flush2StartDate,
          flush: 'Flush 2',
          production: FLUSH_2_TOTAL,
          source: 'room_cycle',
          roomId: cycle.room_id,
          currentDay: currentDay
        });
        
        if (flush2StartDate <= in15Days) {
          forecast15.predicted += FLUSH_2_TOTAL;
          forecast15.flush2 += FLUSH_2_TOTAL;
        }
        if (flush2StartDate <= in30Days) {
          forecast30.predicted += FLUSH_2_TOTAL;
          forecast30.flush2 += FLUSH_2_TOTAL;
        }
        if (flush2StartDate <= in60Days) {
          forecast60.predicted += FLUSH_2_TOTAL;
          forecast60.flush2 += FLUSH_2_TOTAL;
        }
      }
    });
    
    // ===== SOURCE 2: FUTURE SPAWNINGS =====
    // These are projected from composting schedule (not yet in room cycles)
    spawningList.forEach(spawning => {
      // Skip if this spawning is already tracked as a room cycle
      const alreadyInRoomCycle = cycles.some(c => {
        const cycleStart = new Date(c.start_date);
        return Math.abs(cycleStart - spawning.date) < 2 * 24 * 60 * 60 * 1000; // Within 2 days
      });
      
      if (!alreadyInRoomCycle) {
        const flush1Date = addDays(spawning.date, 32); // Day 33
        const flush2Date = addDays(spawning.date, 40); // Day 41
        
        // Add Flush 1
        if (flush1Date >= today) {
          allProductionItems.push({
            batchName: spawning.batchName,
            spawningDate: spawning.date,
            flushDate: flush1Date,
            flush: 'Flush 1',
            production: FLUSH_1_TOTAL,
            source: spawning.source === 'planned' ? 'projected' : 'spawning'
          });
          
          if (flush1Date <= in15Days) {
            forecast15.predicted += FLUSH_1_TOTAL;
            forecast15.flush1 += FLUSH_1_TOTAL;
            forecast15.rooms++;
          }
          if (flush1Date <= in30Days) {
            forecast30.predicted += FLUSH_1_TOTAL;
            forecast30.flush1 += FLUSH_1_TOTAL;
            forecast30.rooms++;
          }
          if (flush1Date <= in60Days) {
            forecast60.predicted += FLUSH_1_TOTAL;
            forecast60.flush1 += FLUSH_1_TOTAL;
            forecast60.rooms++;
          }
        }
        
        // Add Flush 2
        if (flush2Date >= today) {
          allProductionItems.push({
            batchName: spawning.batchName,
            spawningDate: spawning.date,
            flushDate: flush2Date,
            flush: 'Flush 2',
            production: FLUSH_2_TOTAL,
            source: spawning.source === 'planned' ? 'projected' : 'spawning'
          });
          
          if (flush2Date <= in15Days) {
            forecast15.predicted += FLUSH_2_TOTAL;
            forecast15.flush2 += FLUSH_2_TOTAL;
          }
          if (flush2Date <= in30Days) {
            forecast30.predicted += FLUSH_2_TOTAL;
            forecast30.flush2 += FLUSH_2_TOTAL;
          }
          if (flush2Date <= in60Days) {
            forecast60.predicted += FLUSH_2_TOTAL;
            forecast60.flush2 += FLUSH_2_TOTAL;
          }
        }
      }
    });

    // Calculate actual production from production data
    const actual15 = productionData
      .filter(p => {
        const date = new Date(p.harvest_date);
        return date >= addDays(today, -15) && date <= today;
      })
      .reduce((sum, p) => sum + (p.weight_kg || 0), 0);
    
    const actual30 = productionData
      .filter(p => {
        const date = new Date(p.harvest_date);
        return date >= addDays(today, -30) && date <= today;
      })
      .reduce((sum, p) => sum + (p.weight_kg || 0), 0);

    // Month-wise production forecast using allProductionItems (room cycles + spawnings)
    const monthWise = [];
    months.forEach(monthStart => {
      const monthEnd = endOfMonth(monthStart);
      
      // Filter production items for this month
      const productionItems = allProductionItems.filter(item => 
        item.flushDate >= monthStart && item.flushDate <= monthEnd
      );
      
      // Sort by date
      productionItems.sort((a, b) => a.flushDate - b.flushDate);
      
      // Calculate totals
      const monthPredicted = productionItems.reduce((sum, item) => sum + item.production, 0);
      const monthFlush1 = productionItems.filter(i => i.flush === 'Flush 1').reduce((sum, i) => sum + i.production, 0);
      const monthFlush2 = productionItems.filter(i => i.flush === 'Flush 2').reduce((sum, i) => sum + i.production, 0);
      const roomCycleCount = productionItems.filter(i => i.source === 'room_cycle').length;
      const projectedCount = productionItems.filter(i => i.source === 'projected' || i.source === 'spawning').length;
      
      // Get actual production for this month
      const monthActual = productionData
        .filter(p => {
          const date = new Date(p.harvest_date);
          return date >= monthStart && date <= monthEnd;
        })
        .reduce((sum, p) => sum + (p.weight_kg || 0), 0);
      
      // Get spawning count for this month (from spawning forecast)
      const spawningsInMonth = monthSpawnings.find(m => m.month === format(monthStart, 'MMMM yyyy'))?.count || 0;
      
      monthWise.push({
        month: format(monthStart, 'MMMM yyyy'),
        monthShort: format(monthStart, 'MMM'),
        predicted: monthPredicted,
        actual: monthActual,
        flush1: monthFlush1,
        flush2: monthFlush2,
        roomCycles: roomCycleCount,
        projected: projectedCount,
        spawnings: spawningsInMonth,
        productionItems: productionItems
      });
    });

    setProductionForecast({
      next15Days: { predicted: forecast15.predicted, actual: actual15, flush1: forecast15.flush1, flush2: forecast15.flush2, rooms: forecast15.rooms },
      nextMonth: { predicted: forecast30.predicted, actual: actual30, flush1: forecast30.flush1, flush2: forecast30.flush2, rooms: forecast30.rooms },
      next60Days: { predicted: forecast60.predicted, flush1: forecast60.flush1, flush2: forecast60.flush2, rooms: forecast60.rooms },
      monthWise
    });
  };

  // ===== RENDER: Spawning Forecast Card =====
  const SpawningForecastCard = () => (
    <Card data-testid="spawning-forecast-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Sprout className="w-5 h-5 text-green-600" />
          Spawning Forecast (Based on Composting Schedule)
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Month-wise spawning summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {monthWiseSpawnings.map((month) => (
            <div 
              key={month.month}
              className={`p-4 rounded-lg border ${
                month.count > 0 ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
              }`}
            >
              <p className="font-medium text-gray-700">{month.month}</p>
              <p className="text-3xl font-bold text-green-700">{month.count}</p>
              <p className="text-sm text-gray-500">spawnings</p>
            </div>
          ))}
        </div>
        
        {/* Month-wise detailed breakdown */}
        {monthWiseSpawnings.filter(m => m.count > 0).length > 0 ? (
          <div className="space-y-4">
            {monthWiseSpawnings.filter(m => m.count > 0).map((month) => (
              <div key={month.month} className="border rounded-lg overflow-hidden">
                <div className="bg-green-100 px-4 py-2 border-b border-green-200">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-green-800">{month.month}</h4>
                    <Badge className="bg-green-600 text-white">{month.count} spawnings</Badge>
                  </div>
                </div>
                <div className="p-3 bg-white">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {month.spawnings.map((item, sIdx) => (
                      <div 
                        key={item.batchName ? `${item.batchName}-${item.date}` : sIdx}
                        className={`p-2 rounded border text-sm ${
                          item.source === 'planned' ? 'bg-blue-50 border-blue-200' : 'bg-green-50 border-green-200'
                        }`}
                      >
                        <p className="font-medium truncate">{item.batchName}</p>
                        <p className="text-xs text-gray-600">
                          {format(item.date, 'EEE, MMM d')}
                          {item.source === 'planned' && <span className="ml-1 text-blue-600">(Projected)</span>}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">No spawnings scheduled</p>
        )}
      </CardContent>
    </Card>
  );

  // ===== RENDER: Production Forecast Card =====
  const ProductionForecastCard = () => (
    <Card data-testid="production-forecast-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <TrendingUp className="w-5 h-5 text-blue-600" />
          Production Forecast
          {userRole === 'growing_supervisor' && (
            <span className="text-sm font-normal text-gray-500 ml-2">(Based on Composting Schedule)</span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="summary" className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="summary" data-testid="production-summary-tab">Summary</TabsTrigger>
            <TabsTrigger value="monthly" data-testid="production-monthly-tab">Month-wise</TabsTrigger>
          </TabsList>

          <TabsContent value="summary">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* 15 Days */}
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-600 font-medium">Next 15 Days</p>
                <p className="text-2xl font-bold text-blue-800">{(productionForecast.next15Days.predicted / 1000).toFixed(1)}t</p>
                <div className="mt-2 text-xs text-blue-600 space-y-1">
                  <p>From {productionForecast.next15Days.rooms || 0} rooms</p>
                  <p>Flush 1: {(productionForecast.next15Days.flush1 / 1000).toFixed(1)}t</p>
                  <p>Flush 2: {(productionForecast.next15Days.flush2 / 1000).toFixed(1)}t</p>
                </div>
                <div className="mt-2 pt-2 border-t border-blue-200">
                  <p className="text-xs text-blue-700">Actual (Last 15d): <span className="font-bold">{(productionForecast.next15Days.actual / 1000).toFixed(1)}t</span></p>
                </div>
              </div>

              {/* 30 Days */}
              <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                <p className="text-sm text-emerald-600 font-medium">Next Month (30d)</p>
                <p className="text-2xl font-bold text-emerald-800">{(productionForecast.nextMonth.predicted / 1000).toFixed(1)}t</p>
                <div className="mt-2 text-xs text-emerald-600 space-y-1">
                  <p>From {productionForecast.nextMonth.rooms || 0} rooms</p>
                  <p>Flush 1: {(productionForecast.nextMonth.flush1 / 1000).toFixed(1)}t</p>
                  <p>Flush 2: {(productionForecast.nextMonth.flush2 / 1000).toFixed(1)}t</p>
                </div>
                <div className="mt-2 pt-2 border-t border-emerald-200">
                  <p className="text-xs text-emerald-700">Actual (Last 30d): <span className="font-bold">{(productionForecast.nextMonth.actual / 1000).toFixed(1)}t</span></p>
                </div>
              </div>

              {/* 60 Days */}
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                <p className="text-sm text-purple-600 font-medium">Next 60 Days</p>
                <p className="text-2xl font-bold text-purple-800">{(productionForecast.next60Days.predicted / 1000).toFixed(1)}t</p>
                <div className="mt-2 text-xs text-purple-600 space-y-1">
                  <p>From {productionForecast.next60Days.rooms || 0} rooms</p>
                  <p>Flush 1: {(productionForecast.next60Days.flush1 / 1000).toFixed(1)}t</p>
                  <p>Flush 2: {(productionForecast.next60Days.flush2 / 1000).toFixed(1)}t</p>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="monthly">
            <div className="space-y-4">
              {productionForecast.monthWise.filter(m => m.predicted > 0 || m.spawnings > 0).map((month) => (
                <div key={month.month} className="border rounded-lg overflow-hidden">
                  {/* Month Header */}
                  <div className="bg-emerald-100 px-4 py-3 border-b border-emerald-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold text-emerald-800">{month.month}</h4>
                        <p className="text-sm text-emerald-600">
                          {month.roomCycles > 0 && <span className="text-green-700">{month.roomCycles} from current rooms</span>}
                          {month.roomCycles > 0 && month.projected > 0 && ' + '}
                          {month.projected > 0 && <span className="text-blue-600">{month.projected} projected</span>}
                          {month.productionItems?.length > 0 && ` = ${month.productionItems.length} harvests`}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-emerald-700">{(month.predicted / 1000).toFixed(1)}t</p>
                        <div className="flex gap-2 text-xs text-emerald-600">
                          <span>F1: {(month.flush1 / 1000).toFixed(1)}t</span>
                          <span>F2: {(month.flush2 / 1000).toFixed(1)}t</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Production Items Detail */}
                  {month.productionItems?.length > 0 && (
                    <div className="p-3 bg-white">
                      <p className="text-xs font-medium text-gray-500 mb-2">Production breakdown:</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                        {month.productionItems.map((item, pIdx) => (
                          <div 
                            key={item.id || `${item.batch_name || item.batchName || 'item'}-${item.date || pIdx}`}
                            className={`p-2 rounded border text-sm ${
                              item.source === 'room_cycle' 
                                ? (item.flush === 'Flush 1' ? 'bg-green-50 border-green-200' : 'bg-lime-50 border-lime-200')
                                : (item.flush === 'Flush 1' ? 'bg-blue-50 border-blue-200' : 'bg-orange-50 border-orange-200')
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-1">
                                <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                                  item.flush === 'Flush 1' 
                                    ? (item.source === 'room_cycle' ? 'bg-green-200 text-green-800' : 'bg-blue-200 text-blue-800')
                                    : (item.source === 'room_cycle' ? 'bg-lime-200 text-lime-800' : 'bg-orange-200 text-orange-800')
                                }`}>
                                  {item.flush}
                                </span>
                                {item.source === 'room_cycle' && (
                                  <span className="text-xs px-1 py-0.5 bg-green-100 text-green-700 rounded">Active</span>
                                )}
                              </div>
                              <span className="text-xs font-bold">{(item.production / 1000).toFixed(1)}t</span>
                            </div>
                            <p className="font-medium text-gray-800 truncate mt-1">{item.batchName}</p>
                            <p className="text-xs text-gray-500">
                              Harvest: {format(item.flushDate, 'EEE, MMM d')}
                              {item.currentDay && <span className="ml-1">(Day {item.currentDay})</span>}
                            </p>
                            <p className="text-xs text-gray-400">
                              Started: {format(item.spawningDate, 'MMM d')}
                              {item.source === 'projected' && <span className="text-blue-500 ml-1">(Projected)</span>}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Actual vs Predicted */}
                  {month.actual > 0 && (
                    <div className="px-4 py-2 bg-gray-50 border-t text-sm">
                      <span className="text-gray-600">Actual production: </span>
                      <span className="font-bold text-blue-600">{(month.actual / 1000).toFixed(1)}t</span>
                    </div>
                  )}
                </div>
              ))}
              
              {productionForecast.monthWise.filter(m => m.predicted > 0 || m.spawnings > 0).length === 0 && (
                <p className="text-gray-500 text-center py-4">No production forecast available</p>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Explanation for Growing Supervisor */}
        {userRole === 'growing_supervisor' && (
          <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200 text-sm text-gray-600">
            <p className="font-medium mb-1">How this is calculated:</p>
            <p>Based on composting schedule: Prewetting → 20 days composting → Spawning → 32 days to Flush 1 → 40 days to Flush 2</p>
            <p className="mt-1">Each spawning produces: <span className="font-medium">5.5t (Flush 1)</span> + <span className="font-medium">3.5t (Flush 2)</span> = <span className="font-medium">9t total</span></p>
          </div>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return <div className="text-center py-8 text-gray-500">Loading forecast...</div>;
  }

  // ===== OWNER: Tabbed Layout (Spawning | Production | AI Forecast) =====
  if (useTabbedLayout) {
    return (
      <div className="space-y-6" data-testid="forecast-dashboard">
        <Tabs defaultValue="spawning" className="w-full">
          <TabsList className="mb-6 bg-white border border-gray-200">
            <TabsTrigger value="spawning" data-testid="tab-spawning">
              <Sprout className="w-4 h-4 mr-2" />
              Spawning Forecast
            </TabsTrigger>
            <TabsTrigger value="production" data-testid="tab-production">
              <TrendingUp className="w-4 h-4 mr-2" />
              Production Forecast
            </TabsTrigger>
            <TabsTrigger value="standard-yield" data-testid="tab-standard-yield">
              <Activity className="w-4 h-4 mr-2" />
              Standard Yield Curve
            </TabsTrigger>
            <TabsTrigger value="ai" data-testid="tab-ai-forecast">
              <Brain className="w-4 h-4 mr-2" />
              AI Forecast
            </TabsTrigger>
          </TabsList>

          <TabsContent value="spawning">
            <SpawningForecastCard />
          </TabsContent>

          <TabsContent value="production">
            <ProductionForecastCard />
          </TabsContent>

          <TabsContent value="standard-yield">
            <StandardYieldForecast />
          </TabsContent>

          <TabsContent value="ai">
            <AIProductionForecast />
          </TabsContent>
        </Tabs>
      </div>
    );
  }

  // ===== GROWER: Both forecasts on same page =====
  if (userRole === 'grower') {
    return (
      <div className="space-y-6" data-testid="forecast-dashboard">
        <SpawningForecastCard />
        <ProductionForecastCard />
      </div>
    );
  }

  // ===== COMPOSTING SUPERVISOR: Only Spawning Forecast =====
  if (userRole === 'composting_supervisor') {
    return (
      <div className="space-y-6" data-testid="forecast-dashboard">
        <SpawningForecastCard />
      </div>
    );
  }

  // ===== GROWING SUPERVISOR: Only Production Forecast =====
  if (userRole === 'growing_supervisor') {
    return (
      <div className="space-y-6" data-testid="forecast-dashboard">
        <ProductionForecastCard />
      </div>
    );
  }

  // Default fallback
  return (
    <div className="space-y-6" data-testid="forecast-dashboard">
      {showSpawningForecast && <SpawningForecastCard />}
      {showProductionForecast && <ProductionForecastCard />}
    </div>
  );
};

export default ForecastDashboard;
